import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Image, File, Search, Trash2, ExternalLink, Upload,
  FolderOpen, TrendingUp, Clock, User, Hash, LayoutGrid, List,
  Plus, X, Eye, Download, ChevronRight, Paperclip, Edit3, Check, MessageSquare,
  ArrowUpRight
} from 'lucide-react';
import api from '../../api/config';
import DocumentUploadModal from './DocumentUploadModal';

// ── Configuracion de categorias ──
const CATEGORIES = [
  {
    id: 'all',
    label: 'Todos',
    types: ['cotizacion','orden_compra_cliente','orden_compra_proveedor','guia_despacho','factura_cliente','factura_proveedor','otro'],
    icon: FolderOpen,
    gradient: null,
    useTheme: true
  },
  {
    id: 'cotizaciones',
    label: 'Cotizaciones',
    types: ['cotizacion'],
    icon: FileText,
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
  },
  {
    id: 'ordenes',
    label: 'Ordenes de Compra',
    types: ['orden_compra_cliente', 'orden_compra_proveedor'],
    icon: Paperclip,
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
  },
  {
    id: 'despacho',
    label: 'Guias Despacho',
    types: ['guia_despacho'],
    icon: TrendingUp,
    gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)'
  },
  {
    id: 'facturas',
    label: 'Facturas',
    types: ['factura_cliente', 'factura_proveedor'],
    icon: Hash,
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
  },
  {
    id: 'otros',
    label: 'Otros',
    types: ['otro'],
    icon: File,
    gradient: 'linear-gradient(135deg, #c3cfe2 0%, #f5f7fa 100%)'
  }
];

const DOC_TYPE_LABELS = {
  cotizacion: 'Cotizacion',
  orden_compra_cliente: 'OC Cliente',
  orden_compra_proveedor: 'OC Proveedor',
  guia_despacho: 'Guia Despacho',
  factura_cliente: 'Factura Cliente',
  factura_proveedor: 'Factura Proveedor',
  otro: 'Otro'
};

const MULTI_DOC_TYPES = new Set([
  'guia_despacho', 'factura_cliente', 'factura_proveedor'
]);

// Mapeo inverso: doc_type → campo del maestro (para sincronizar referencia)
const DOC_TYPE_TO_FIELD = {
  cotizacion: 'cotizacion',
  orden_compra_cliente: 'p_order',
  orden_compra_proveedor: 'oc_sobek',
  guia_despacho: 'guia',
  factura_cliente: 'factura',
  factura_proveedor: 'factura_proveedor'
};

const DOC_TYPE_COLORS = {
  cotizacion: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' },
  orden_compra_cliente: { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
  orden_compra_proveedor: { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  guia_despacho: { bg: '#f3e8ff', text: '#6b21a8', border: '#d8b4fe' },
  factura_cliente: { bg: '#fce7f3', text: '#9d174d', border: '#fbcfe8' },
  factura_proveedor: { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa' },
  otro: { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' }
};

const DocumentosPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [showItemModal, setShowItemModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadItemNumber, setUploadItemNumber] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null); // { id, doc_reference, description }
  const [editSaving, setEditSaving] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [activeCategory, debouncedSearch]);

  const loadStats = async () => {
    try {
      const response = await api.get('/documents/stats');
      setStats(response.data || {});
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const cat = CATEGORIES.find(c => c.id === activeCategory);
      const allDocs = [];
      for (const type of cat.types) {
        const response = await api.get(`/documents/by-type/${type}`, {
          params: { search: debouncedSearch || undefined, limit: 200 }
        });
        if (response.data?.documents) allDocs.push(...response.data.documents);
        else if (Array.isArray(response.data)) allDocs.push(...response.data);
      }
      allDocs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setDocuments(allDocs);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (docId, e) => {
    e?.stopPropagation();
    if (!confirm('¿Eliminar este documento permanentemente?')) return;
    setDeletingId(docId);
    try {
      await api.delete(`/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      loadStats();
    } catch (error) {
      alert('Error al eliminar documento');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUploadSuccess = () => {
    loadDocuments();
    loadStats();
    setShowUploadModal(false);
    setUploadItemNumber('');
  };

  const handleEditSave = async () => {
    if (!editingDoc) return;
    setEditSaving(true);
    try {
      await api.patch(`/documents/${editingDoc.id}`, {
        doc_reference: editingDoc.doc_reference,
        description: editingDoc.description
      });
      // Sincronizar referencia con el maestro (solo para single-doc)
      // Multi-doc tiene refs individuales, no se sincronizan al maestro
      const fieldKey = DOC_TYPE_TO_FIELD[editingDoc.doc_type];
      if (fieldKey && editingDoc.item_number && !MULTI_DOC_TYPES.has(editingDoc.doc_type)) {
        api.patch('/documents/sync-maestro-field', {
          item_number: editingDoc.item_number,
          field_key: fieldKey,
          value: editingDoc.doc_reference || ''
        }).catch(err => console.error('Error syncing maestro:', err));
        api.patch('/documents/update-reference', {
          item_number: editingDoc.item_number,
          doc_type: editingDoc.doc_type,
          new_reference: editingDoc.doc_reference || ''
        }).catch(err => console.error('Error syncing doc refs:', err));
      }
      setDocuments(prev => prev.map(d =>
        d.id === editingDoc.id
          ? { ...d, doc_reference: editingDoc.doc_reference, description: editingDoc.description }
          : d
      ));
      setEditingDoc(null);
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error al guardar cambios');
    } finally {
      setEditSaving(false);
    }
  };

  const getFileIcon = (mimeType, size = 24) => {
    if (mimeType === 'application/pdf') return <FileText size={size} style={{ color: '#ef4444' }} />;
    if (mimeType?.startsWith('image/')) return <Image size={size} style={{ color: '#3b82f6' }} />;
    return <File size={size} style={{ color: '#6b7280' }} />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Hace un momento';
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `Hace ${Math.floor(diff / 86400000)}d`;
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CL', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getCategoryCount = (cat) => {
    return cat.types.reduce((sum, type) => sum + (stats[type] || 0), 0);
  };

  const totalDocs = Object.values(stats).reduce((sum, v) => sum + v, 0);

  const isPreviewable = (mimeType) => {
    return mimeType === 'application/pdf' || mimeType?.startsWith('image/');
  };

  return (
    <div style={{
      padding: '28px 32px',
      fontFamily: "'Space Grotesk', sans-serif",
      maxWidth: '1440px',
      margin: '0 auto',
      minHeight: '100vh'
    }}>

      {/* ─── HEADER ─── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: '28px', flexWrap: 'wrap', gap: '16px'
      }}>
        <div>
          <h1 style={{
            margin: 0, fontSize: '28px', fontWeight: 900, color: '#000',
            letterSpacing: '-0.5px'
          }}>
            Documentos
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>
            Gestion centralizada de archivos vinculados al Maestro
          </p>
        </div>
        <button
          onClick={() => setShowItemModal(true)}
          style={{
            padding: '12px 24px',
            background: 'var(--theme-secondary, #000)',
            color: 'var(--theme-primary, #c4fffc)',
            border: '2px solid var(--theme-secondary, #000)',
            borderRadius: '12px', fontSize: '14px', fontWeight: 700,
            cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
            display: 'flex', alignItems: 'center', gap: '8px',
            transition: 'all 0.25s ease',
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.15)';
          }}
        >
          <Plus size={18} />
          Subir Documento
        </button>
      </div>

      {/* ─── STATS BENTO GRID ─── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '28px'
      }}>
        {CATEGORIES.map(cat => {
          const count = getCategoryCount(cat);
          const isActive = activeCategory === cat.id;
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              style={{
                position: 'relative',
                padding: '18px 16px',
                border: isActive ? '2px solid var(--theme-secondary, #000)' : '2px solid #e5e7eb',
                borderRadius: '16px',
                background: isActive ? 'var(--theme-primary, #c4fffc)' : 'white',
                cursor: 'pointer',
                fontFamily: "'Space Grotesk', sans-serif",
                textAlign: 'left',
                transition: 'all 0.25s ease',
                overflow: 'hidden',
                boxShadow: isActive ? '0 4px 20px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
                transform: isActive ? 'scale(1.02)' : 'scale(1)'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.borderColor = '#9ca3af';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              {/* Gradient accent */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                background: cat.useTheme ? 'var(--theme-secondary, #000)' : cat.gradient,
                borderRadius: '16px 16px 0 0'
              }} />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  background: cat.useTheme ? 'var(--theme-secondary, #000)' : cat.gradient,
                  display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <Icon size={18} style={{ color: cat.useTheme ? 'var(--theme-primary, #c4fffc)' : 'white' }} />
                </div>
                <span style={{
                  fontSize: '28px', fontWeight: 900, color: isActive ? 'var(--theme-secondary, #000)' : '#1f2937',
                  lineHeight: 1
                }}>
                  {count}
                </span>
              </div>
              <div style={{
                fontSize: '12px', fontWeight: 600,
                color: isActive ? 'var(--theme-secondary, #000)' : '#6b7280',
                letterSpacing: '0.3px'
              }}>
                {cat.label}
              </div>
            </button>
          );
        })}
      </div>

      {/* ─── TOOLBAR: Search + View Toggle ─── */}
      <div style={{
        display: 'flex', gap: '12px', marginBottom: '20px',
        alignItems: 'center', flexWrap: 'wrap'
      }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '280px' }}>
          <Search size={16} style={{
            position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
            color: '#9ca3af', pointerEvents: 'none'
          }} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por referencia, archivo, cliente o proveedor..."
            style={{
              width: '100%', padding: '11px 14px 11px 40px',
              border: '2px solid #e5e7eb', borderRadius: '12px',
              fontSize: '14px', fontFamily: "'Space Grotesk', sans-serif",
              boxSizing: 'border-box', transition: 'border-color 0.2s',
              outline: 'none', background: '#fafafa'
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--theme-secondary, #000)'; e.target.style.background = 'white'; }}
            onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#fafafa'; }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                display: 'flex', alignItems: 'center'
              }}
            >
              <X size={14} style={{ color: '#9ca3af' }} />
            </button>
          )}
        </div>

        {/* View Mode Toggle */}
        <div style={{
          display: 'flex', background: '#f3f4f6', borderRadius: '10px', padding: '3px',
          border: '1px solid #e5e7eb'
        }}>
          {[
            { mode: 'grid', icon: LayoutGrid },
            { mode: 'list', icon: List }
          ].map(({ mode, icon: Icon }) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                padding: '8px 10px', border: 'none', borderRadius: '8px',
                background: viewMode === mode ? 'var(--theme-secondary, #000)' : 'transparent',
                color: viewMode === mode ? 'var(--theme-primary, #c4fffc)' : '#6b7280',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                transition: 'all 0.2s'
              }}
            >
              <Icon size={16} />
            </button>
          ))}
        </div>

        {/* Results count */}
        <span style={{ fontSize: '13px', color: '#9ca3af', fontWeight: 500, whiteSpace: 'nowrap' }}>
          {documents.length} resultado{documents.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ─── CONTENT ─── */}
      {loading ? (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '80px 0', gap: '16px'
        }}>
          <div style={{
            width: '40px', height: '40px', border: '3px solid #e5e7eb',
            borderTop: '3px solid var(--theme-secondary, #000)',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite'
          }} />
          <span style={{ color: '#9ca3af', fontSize: '14px' }}>Cargando documentos...</span>
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
            .doc-pdf-scroll::-webkit-scrollbar { width: 5px; }
            .doc-pdf-scroll::-webkit-scrollbar-track { background: transparent; }
            .doc-pdf-scroll::-webkit-scrollbar-thumb { background: var(--theme-secondary, #000); border-radius: 10px; }
            .doc-pdf-scroll::-webkit-scrollbar-thumb:hover { background: var(--theme-secondary, #333); }
            .doc-pdf-scroll { scrollbar-width: thin; scrollbar-color: var(--theme-secondary, #000) transparent; }
          `}</style>
        </div>
      ) : documents.length === 0 ? (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '80px 20px',
          background: '#fafafa', borderRadius: '20px', border: '2px dashed #e5e7eb'
        }}>
          <div style={{
            width: '72px', height: '72px', borderRadius: '20px',
            background: 'var(--theme-secondary, #000)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '20px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
          }}>
            <FolderOpen size={32} style={{ color: 'var(--theme-primary, #c4fffc)' }} />
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#1f2937' }}>
            {searchTerm ? 'Sin resultados' : 'No hay documentos'}
          </h3>
          <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#9ca3af', textAlign: 'center', maxWidth: '400px' }}>
            {searchTerm
              ? `No se encontraron documentos para "${searchTerm}". Intenta con otro termino.`
              : 'Sube documentos desde el Maestro haciendo clic en el icono de clip, o usa el boton "Subir Documento" arriba.'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowItemModal(true)}
              style={{
                padding: '10px 24px', background: 'var(--theme-secondary, #000)',
                color: 'var(--theme-primary, #c4fffc)',
                border: '2px solid var(--theme-secondary, #000)',
                borderRadius: '10px', fontSize: '14px', fontWeight: 700,
                cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
                display: 'flex', alignItems: 'center', gap: '8px'
              }}
            >
              <Upload size={16} /> Subir primer documento
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        /* ─── GRID VIEW (Cards) ─── */
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '16px'
        }}>
          {documents.map(doc => {
            const typeColor = DOC_TYPE_COLORS[doc.doc_type] || DOC_TYPE_COLORS.otro;
            return (
              <div
                key={doc.id}
                style={{
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'all 0.25s ease',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--theme-secondary, #000)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.08)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                onClick={() => {
                  if (isPreviewable(doc.mime_type)) setPreviewDoc(doc);
                  else window.open(doc.url, '_blank');
                }}
              >
                {/* Card Top - File Preview Area */}
                <div
                  style={{
                    height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: doc.mime_type?.startsWith('image/')
                      ? `url(${doc.url}) center/cover no-repeat`
                      : doc.mime_type === 'application/pdf'
                      ? '#f8fafc'
                      : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderBottom: '1px solid #f1f5f9',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onClick={() => {
                    if (isPreviewable(doc.mime_type)) setPreviewDoc(doc);
                    else window.open(doc.url, '_blank');
                  }}
                >
                  {doc.mime_type === 'application/pdf' ? (
                    <div
                      className="doc-pdf-scroll"
                      style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        overflowY: 'auto', overflowX: 'hidden'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <iframe
                        src={`${doc.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                        title={doc.original_name}
                        scrolling="no"
                        style={{
                          width: 'calc(100% + 20px)', height: '600px',
                          border: 'none', background: '#f8fafc', display: 'block'
                        }}
                      />
                    </div>
                  ) : (
                    !doc.mime_type?.startsWith('image/') && getFileIcon(doc.mime_type, 40)
                  )}
                </div>

                {/* Card Body */}
                <div style={{ padding: '12px 14px 14px' }}>
                  {/* Top: Actions */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}
                    onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{
                        background: 'var(--theme-primary, #c4fffc)',
                        border: '1.5px solid var(--theme-secondary, #000)',
                        borderRadius: '6px', padding: '2px 8px',
                        fontSize: '10px', fontWeight: 800, color: 'var(--theme-secondary, #000)'
                      }}>
                        Item #{doc.item_number}
                      </span>
                      <span style={{
                        background: typeColor.bg, border: `1px solid ${typeColor.border}`,
                        borderRadius: '6px', padding: '2px 8px',
                        fontSize: '10px', fontWeight: 700, color: typeColor.text
                      }}>
                        {DOC_TYPE_LABELS[doc.doc_type]}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                      {DOC_TYPE_TO_FIELD[doc.doc_type] && (
                        <button onClick={() => navigate('/matriz', { state: { scrollToDoc: { itemNumber: doc.item_number, fieldKey: DOC_TYPE_TO_FIELD[doc.doc_type] } } })}
                          title="Ver en Maestro" style={{
                            width: '28px', height: '28px', borderRadius: '7px',
                            background: 'var(--theme-primary, #c4fffc)', border: '1.5px solid var(--theme-secondary, #000)',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                          <ArrowUpRight size={12} style={{ color: 'var(--theme-secondary, #000)' }} />
                        </button>
                      )}
                      <button onClick={() => setEditingDoc({ id: doc.id, doc_reference: doc.doc_reference || '', description: doc.description || '', item_number: doc.item_number, doc_type: doc.doc_type })}
                        title="Editar" style={{
                          width: '28px', height: '28px', borderRadius: '7px',
                          background: '#f0fdf4', border: '1px solid #bbf7d0', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                        <Edit3 size={12} style={{ color: '#16a34a' }} />
                      </button>
                      <a href={doc.url} download={doc.original_name} title="Descargar" style={{
                          width: '28px', height: '28px', borderRadius: '7px',
                          background: '#f0f9ff', border: '1px solid #bae6fd',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none'
                        }}>
                        <Download size={12} style={{ color: '#0284c7' }} />
                      </a>
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" title="Abrir" style={{
                          width: '28px', height: '28px', borderRadius: '7px',
                          background: '#eff6ff', border: '1px solid #bfdbfe',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none'
                        }}>
                        <ExternalLink size={12} style={{ color: '#2563eb' }} />
                      </a>
                      <button onClick={(e) => handleDelete(doc.id, e)} disabled={deletingId === doc.id}
                        title="Eliminar" style={{
                          width: '28px', height: '28px', borderRadius: '7px',
                          background: '#fef2f2', border: '1px solid #fecaca', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: deletingId === doc.id ? 0.5 : 1
                        }}>
                        <Trash2 size={12} style={{ color: '#dc2626' }} />
                      </button>
                    </div>
                  </div>

                  <div style={{
                    fontWeight: 700, fontSize: '13px', color: '#1f2937',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    marginBottom: '6px'
                  }}>
                    {doc.original_name}
                  </div>

                  {/* Meta info */}
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '10px',
                    fontSize: '11px', color: '#9ca3af', marginBottom: '8px'
                  }}>
                    {doc.doc_reference && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Hash size={11} /> {doc.doc_reference}
                      </span>
                    )}
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Clock size={11} /> {formatDate(doc.created_at)}
                    </span>
                    <span>{formatFileSize(doc.file_size)}</span>
                  </div>

                  {/* Descripción del documento */}
                  {doc.description && (
                    <div style={{
                      fontSize: '11px', color: '#4b5563', padding: '6px 10px',
                      background: '#fef9c3', borderRadius: '8px', marginBottom: '8px',
                      display: 'flex', alignItems: 'flex-start', gap: '6px',
                      border: '1px solid #fde68a', lineHeight: 1.4
                    }}>
                      <MessageSquare size={11} style={{ color: '#92400e', flexShrink: 0, marginTop: '1px' }} />
                      <span>{doc.description}</span>
                    </div>
                  )}

                  {/* Info del maestro */}
                  {(doc.cliente || doc.proveedor || doc.item_descripcion) && (
                    <div style={{
                      fontSize: '11px', color: '#6b7280', padding: '6px 10px',
                      background: '#f9fafb', borderRadius: '8px', marginBottom: '8px',
                      display: 'flex', flexDirection: 'column', gap: '2px'
                    }}>
                      {doc.item_descripcion && <span style={{ fontWeight: 600, color: '#374151' }}>{doc.item_descripcion}</span>}
                      {doc.cliente && <span><strong>Cliente:</strong> {doc.cliente}</span>}
                      {doc.proveedor && <span><strong>Prov:</strong> {doc.proveedor}</span>}
                    </div>
                  )}

                  {/* Footer: User */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                      width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                      background: 'var(--theme-secondary, #000)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <User size={11} style={{ color: 'var(--theme-primary, #c4fffc)' }} />
                    </div>
                    <span style={{
                      fontSize: '11px', fontWeight: 600, color: '#6b7280',
                      wordBreak: 'break-all', lineHeight: 1.3
                    }}>
                      {doc.uploaded_by_name || 'Usuario'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ─── LIST VIEW ─── */
        <div style={{
          border: '2px solid #e5e7eb', borderRadius: '16px', overflow: 'hidden',
          background: 'white'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={thStyle}>Archivo</th>
                <th style={thStyle}>Tipo</th>
                <th style={thStyle}>Ref.</th>
                <th style={thStyle}>Item</th>
                <th style={thStyle}>Cliente / Proveedor</th>
                <th style={thStyle}>Subido por</th>
                <th style={thStyle}>Fecha</th>
                <th style={{ ...thStyle, textAlign: 'center', width: '165px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => {
                const typeColor = DOC_TYPE_COLORS[doc.doc_type] || DOC_TYPE_COLORS.otro;
                return (
                  <tr
                    key={doc.id}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      transition: 'background 0.15s', cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#fafafa'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    onClick={() => {
                      if (isPreviewable(doc.mime_type)) setPreviewDoc(doc);
                      else window.open(doc.url, '_blank');
                    }}
                  >
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '10px',
                          background: '#f8fafc', border: '1px solid #e5e7eb',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                          {getFileIcon(doc.mime_type, 18)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontWeight: 600, fontSize: '13px', color: '#1f2937',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            maxWidth: '220px'
                          }}>
                            {doc.original_name}
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {formatFileSize(doc.file_size)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        fontSize: '11px', fontWeight: 700,
                        padding: '3px 10px', borderRadius: '8px',
                        background: typeColor.bg, color: typeColor.text,
                        border: `1px solid ${typeColor.border}`
                      }}>
                        {DOC_TYPE_LABELS[doc.doc_type]}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '12px', color: '#374151', fontWeight: 500 }}>
                        {doc.doc_reference || '-'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        fontWeight: 800, fontSize: '12px',
                        background: 'var(--theme-primary, #c4fffc)',
                        padding: '3px 10px', borderRadius: '8px',
                        border: '1.5px solid var(--theme-secondary, #000)',
                        color: 'var(--theme-secondary, #000)'
                      }}>
                        #{doc.item_number}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: '12px' }}>
                        {doc.item_descripcion && <div style={{ fontWeight: 600, color: '#374151', fontSize: '11px', marginBottom: '2px' }}>{doc.item_descripcion}</div>}
                        {doc.cliente && <div style={{ fontWeight: 500 }}>{doc.cliente}</div>}
                        {doc.proveedor && <div style={{ color: '#9ca3af', fontSize: '11px' }}>{doc.proveedor}</div>}
                        {!doc.cliente && !doc.proveedor && !doc.item_descripcion && <span style={{ color: '#d1d5db' }}>-</span>}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                          width: '22px', height: '22px', borderRadius: '50%',
                          background: 'var(--theme-secondary, #000)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                          <User size={10} style={{ color: 'var(--theme-primary, #c4fffc)' }} />
                        </div>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                          {doc.uploaded_by_name || '-'}
                        </span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }} title={formatDateFull(doc.created_at)}>
                        {formatDate(doc.created_at)}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        {DOC_TYPE_TO_FIELD[doc.doc_type] && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/matriz', { state: { scrollToDoc: { itemNumber: doc.item_number, fieldKey: DOC_TYPE_TO_FIELD[doc.doc_type] } } });
                            }}
                            title="Ver en Maestro"
                            style={{
                              width: '30px', height: '30px', borderRadius: '8px',
                              background: 'var(--theme-primary, #c4fffc)',
                              border: '1.5px solid var(--theme-secondary, #000)', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                            <ArrowUpRight size={13} style={{ color: 'var(--theme-secondary, #000)' }} />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingDoc({ id: doc.id, doc_reference: doc.doc_reference || '', description: doc.description || '', item_number: doc.item_number, doc_type: doc.doc_type });
                          }}
                          title="Editar info"
                          style={{
                            width: '30px', height: '30px', borderRadius: '8px',
                            background: '#f0fdf4', border: '1px solid #bbf7d0', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                          <Edit3 size={13} style={{ color: '#16a34a' }} />
                        </button>
                        <a href={doc.url} download={doc.original_name} title="Descargar"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '30px', height: '30px', borderRadius: '8px',
                            background: '#f0f9ff', border: '1px solid #bae6fd',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            textDecoration: 'none'
                          }}>
                          <Download size={13} style={{ color: '#0284c7' }} />
                        </a>
                        <a href={doc.url} target="_blank" rel="noopener noreferrer" title="Abrir"
                          style={{
                            width: '30px', height: '30px', borderRadius: '8px',
                            background: '#eff6ff', border: '1px solid #bfdbfe',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            textDecoration: 'none'
                          }}>
                          <ExternalLink size={13} style={{ color: '#2563eb' }} />
                        </a>
                        <button onClick={(e) => handleDelete(doc.id, e)} disabled={deletingId === doc.id}
                          title="Eliminar"
                          style={{
                            width: '30px', height: '30px', borderRadius: '8px',
                            background: '#fef2f2', border: '1px solid #fecaca', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: deletingId === doc.id ? 0.5 : 1
                          }}>
                          <Trash2 size={13} style={{ color: '#dc2626' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── PREVIEW MODAL ─── */}
      {previewDoc && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', backdropFilter: 'blur(4px)'
          }}
          onClick={() => setPreviewDoc(null)}
        >
          <div
            style={{
              background: 'white', borderRadius: '20px',
              width: '90vw', maxWidth: '900px', height: '85vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 25px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Preview Header */}
            <div style={{
              padding: '16px 20px', borderBottom: '2px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                {getFileIcon(previewDoc.mime_type, 22)}
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontWeight: 700, fontSize: '14px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {previewDoc.original_name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                    {DOC_TYPE_LABELS[previewDoc.doc_type]} | Item #{previewDoc.item_number} | {formatFileSize(previewDoc.file_size)}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <a href={previewDoc.url} target="_blank" rel="noopener noreferrer"
                  style={{
                    padding: '8px 16px', background: '#eff6ff', border: '1px solid #bfdbfe',
                    borderRadius: '8px', fontSize: '12px', fontWeight: 600, color: '#2563eb',
                    textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px'
                  }}>
                  <ExternalLink size={14} /> Abrir
                </a>
                <button onClick={() => setPreviewDoc(null)}
                  style={{
                    width: '36px', height: '36px', borderRadius: '10px',
                    background: '#f3f4f6', border: '1px solid #e5e7eb', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                  <X size={18} />
                </button>
              </div>
            </div>
            {/* Preview Content */}
            <div style={{ flex: 1, overflow: 'hidden', background: '#f8fafc' }}>
              {previewDoc.mime_type === 'application/pdf' ? (
                <iframe
                  src={previewDoc.url}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="PDF Preview"
                />
              ) : previewDoc.mime_type?.startsWith('image/') ? (
                <div style={{
                  width: '100%', height: '100%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: '20px'
                }}>
                  <img
                    src={previewDoc.url}
                    alt={previewDoc.original_name}
                    style={{
                      maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
                      borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* ─── ITEM NUMBER MODAL ─── */}
      {showItemModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(4px)'
          }}
        >
          <div style={{
            background: 'white', borderRadius: '20px', width: '420px', maxWidth: '95vw',
            padding: '28px', boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
            fontFamily: "'Space Grotesk', sans-serif"
          }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '14px',
              background: 'var(--theme-secondary, #000)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <Upload size={22} style={{ color: 'var(--theme-primary, #c4fffc)' }} />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 800 }}>
              Vincular a Item del Maestro
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 20px', lineHeight: 1.5 }}>
              Ingresa el numero de item al que deseas vincular el documento. Puedes encontrarlo en la columna "#" del Maestro.
            </p>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#374151', display: 'block', marginBottom: '6px' }}>
                N° de Item
              </label>
              <input
                type="number"
                min="1"
                value={uploadItemNumber}
                onChange={(e) => setUploadItemNumber(e.target.value)}
                placeholder="Ej: 1, 2, 3..."
                style={{
                  width: '100%', padding: '12px 14px', border: '2px solid #e5e7eb',
                  borderRadius: '12px', fontSize: '16px', fontWeight: 600,
                  fontFamily: "'Space Grotesk', sans-serif",
                  boxSizing: 'border-box', outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => { e.target.style.borderColor = 'var(--theme-secondary, #000)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && uploadItemNumber && parseInt(uploadItemNumber) > 0) {
                    setShowItemModal(false);
                    setShowUploadModal(true);
                  }
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setShowItemModal(false); setUploadItemNumber(''); }}
                style={{
                  flex: 1, padding: '12px', border: '2px solid #e5e7eb',
                  borderRadius: '12px', background: 'white', cursor: 'pointer',
                  fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '14px', transition: 'all 0.2s'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (uploadItemNumber && parseInt(uploadItemNumber) > 0) {
                    setShowItemModal(false);
                    setShowUploadModal(true);
                  }
                }}
                disabled={!uploadItemNumber || parseInt(uploadItemNumber) <= 0}
                style={{
                  flex: 1, padding: '12px',
                  background: (!uploadItemNumber || parseInt(uploadItemNumber) <= 0) ? '#e5e7eb' : 'var(--theme-secondary, #000)',
                  color: (!uploadItemNumber || parseInt(uploadItemNumber) <= 0) ? '#9ca3af' : 'var(--theme-primary, #c4fffc)',
                  border: '2px solid var(--theme-secondary, #000)',
                  borderRadius: '12px', fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Space Grotesk', sans-serif", fontSize: '14px',
                  transition: 'all 0.2s', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '6px'
                }}
              >
                Continuar <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── EDIT MODAL ─── */}
      {editingDoc && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setEditingDoc(null)}
        >
          <div
            style={{
              background: 'white', borderRadius: '20px', width: '440px', maxWidth: '95vw',
              padding: '28px', boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
              fontFamily: "'Space Grotesk', sans-serif"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '42px', height: '42px', borderRadius: '12px',
                background: 'var(--theme-secondary, #000)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Edit3 size={20} style={{ color: 'var(--theme-primary, #c4fffc)' }} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800 }}>Editar Documento</h3>
                <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>Modifica la referencia o descripcion</p>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#374151', display: 'block', marginBottom: '6px' }}>
                Referencia / N° Documento
              </label>
              <input
                type="text"
                value={editingDoc.doc_reference}
                onChange={(e) => setEditingDoc(prev => ({ ...prev, doc_reference: e.target.value }))}
                placeholder="Ej: COT-2026-001, FAC-123..."
                style={{
                  width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb',
                  borderRadius: '10px', fontSize: '14px', fontWeight: 500,
                  fontFamily: "'Space Grotesk', sans-serif",
                  boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s'
                }}
                onFocus={(e) => { e.target.style.borderColor = 'var(--theme-secondary, #000)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#374151', display: 'block', marginBottom: '6px' }}>
                Descripcion
              </label>
              <textarea
                value={editingDoc.description}
                onChange={(e) => setEditingDoc(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Agrega una nota o descripcion del documento..."
                rows={3}
                style={{
                  width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb',
                  borderRadius: '10px', fontSize: '13px',
                  fontFamily: "'Space Grotesk', sans-serif",
                  boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s',
                  resize: 'vertical', minHeight: '70px'
                }}
                onFocus={(e) => { e.target.style.borderColor = 'var(--theme-secondary, #000)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setEditingDoc(null)}
                style={{
                  flex: 1, padding: '11px', border: '2px solid #e5e7eb',
                  borderRadius: '10px', background: 'white', cursor: 'pointer',
                  fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '14px', transition: 'all 0.2s'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleEditSave}
                disabled={editSaving}
                style={{
                  flex: 1, padding: '11px',
                  background: 'var(--theme-secondary, #000)',
                  color: 'var(--theme-primary, #c4fffc)',
                  border: '2px solid var(--theme-secondary, #000)',
                  borderRadius: '10px', fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Space Grotesk', sans-serif", fontSize: '14px',
                  transition: 'all 0.2s', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '6px',
                  opacity: editSaving ? 0.7 : 1
                }}
              >
                <Check size={16} />
                {editSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── UPLOAD MODAL ─── */}
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => { setShowUploadModal(false); setUploadItemNumber(''); }}
        itemNumber={parseInt(uploadItemNumber) || 0}
        itemData={null}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
};

const thStyle = {
  padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 700,
  color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px',
  fontFamily: "'Space Grotesk', sans-serif"
};

const tdStyle = {
  padding: '14px 16px', fontSize: '13px', color: '#1f2937',
  fontFamily: "'Space Grotesk', sans-serif"
};

export default DocumentosPage;
